import { useEffect } from "react";
import { PIXEL_ID } from "./constants";

type PixelFunction = {
  (action: string, event: string, params?: Record<string, unknown>): void;
  callMethod?: (action: string, event: string, params?: Record<string, unknown>) => void;
  loaded?: boolean;
  version?: string;
  queue?: Array<[string, string, Record<string, unknown> | undefined]>;
};

type PixelWindow = Window & {
  fbq?: PixelFunction;
  _fbq?: PixelFunction;
};

function createPixelQueue(pixelWindow: PixelWindow) {
  const existing = pixelWindow.fbq;
  if (existing) return existing;

  const queue: Array<[string, string, Record<string, unknown> | undefined]> = [];
  const pixel = ((action: string, event: string, params?: Record<string, unknown>) => {
    if (pixel.callMethod) {
      pixel.callMethod(action, event, params);
      return;
    }
    queue.push([action, event, params]);
  }) as PixelFunction;

  pixel.queue = queue;
  pixel.loaded = true;
  pixel.version = "2.0";
  pixelWindow.fbq = pixel;
  pixelWindow._fbq = pixel;
  return pixel;
}

// Tracking must never block the customer experience if a browser blocks Meta scripts.
export function useMetaPixel() {
  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return;

    try {
      const pixelWindow = window as PixelWindow;
      const pixel = createPixelQueue(pixelWindow);
      pixel("init", PIXEL_ID);
      pixel("track", "PageView");

      if (document.getElementById("meta-pixel-script")) return;

      const script = document.createElement("script");
      script.id = "meta-pixel-script";
      script.async = true;
      script.src = "https://connect.facebook.net/en_US/fbevents.js";
      script.onerror = () => {
        // Blocked trackers are expected in previews and privacy-focused browsers.
        script.remove();
      };
      document.head.appendChild(script);
    } catch {
      // Keep the storefront functional even when the tracking environment is unavailable.
    }
  }, []);
}

export function trackPixel(event: string, params?: Record<string, unknown>) {
  try {
    const pixelWindow = window as PixelWindow;
    pixelWindow.fbq?.("track", event, params);
  } catch {
    // Checkout navigation must continue even when tracking is unavailable.
  }
}
